import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DtcHelpButton } from "@/components/TechGuide";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Download, ExternalLink,
  Car, Building2, Shield, Hash, User,
  X, ChevronRight, Briefcase, FileCheck, Plus, Pencil, Check, Sparkles, Wrench,
  Search, BookOpen, Trash2, Link2, Lightbulb, Copy, Target, RefreshCw,
} from "lucide-react";

const SECTION_ACCENTS = {
  ADAS: { bar: "bg-blue-500", text: "text-blue-300", border: "border-blue-500/30" },
  "Post Wheel Alignment": { bar: "bg-amber-500", text: "text-amber-300", border: "border-amber-500/30" },
  Safety: { bar: "bg-emerald-500", text: "text-emerald-300", border: "border-emerald-500/30" },
};

// ──────────────────────────────────────────────────────────────
// ALLDATA Bridge Mode — build smart search URLs from vehicle + op
// ──────────────────────────────────────────────────────────────
function buildAllDataSearchUrl(vehicle, opName) {
  const make = vehicle?.make || "";
  const model = vehicle?.model || "";
  const year = vehicle?.year || "";
  // ALLDATA standard subscription doesn't accept VIN deep-links via URL,
  // but its search page accepts a generic ?searchString= param.
  const q = `${year} ${make} ${model} ${opName || ""}`.trim();
  return `https://my.alldata.com/repair/#/search?searchString=${encodeURIComponent(q)}`;
}
function buildGoogleOemUrl(vehicle, opName) {
  const q = `${vehicle?.year || ""} ${vehicle?.make || ""} ${vehicle?.model || ""} ${opName || ""} OEM procedure position statement`.trim();
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
}

// ──────────────────────────────────────────────────────────────
// ALLDATA Path Helper — tells employees EXACTLY where to click
// inside ALLDATA to find the procedure URL for each ADAS op.
// ──────────────────────────────────────────────────────────────
// Each entry: keywords (lowercase) → navigation breadcrumbs +
// recommended search terms + document hint.
const ALLDATA_PATH_RULES = [
  {
    match: ["front camera", "windshield camera", "forward camera", "lkas", "lane keep", "lane departure", "lanewatch", "lane watch"],
    label: "Front / Windshield Camera",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Front Camera Calibration"],
    searchTerms: ["front camera calibration", "windshield camera initialization", "lane keep assist initialization"],
    docHint: 'Look for a document titled "Calibration" or "Initialization" (NOT "Removal & Installation").',
  },
  {
    match: ["front radar", "millimeter wave", "acc radar", "adaptive cruise", "distance sensor", "forward radar"],
    label: "Front Radar / Adaptive Cruise",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Forward Radar / ACC Sensor"],
    searchTerms: ["front radar aiming", "ACC sensor alignment", "adaptive cruise initialization", "millimeter wave radar"],
    docHint: 'Look for "Aiming", "Alignment" or "Initialization" — usually requires target board or auto-aim.',
  },
  {
    match: ["blind spot", "bsm", "bsi", "side radar", "rear radar"],
    label: "Blind Spot Monitor (BSM)",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Blind Spot Information System"],
    searchTerms: ["blind spot calibration", "BSM initialization", "rear corner radar"],
    docHint: 'Search for "Blind Spot" — procedure may be under Side Radar or Rear Corner Radar.',
  },
  {
    match: ["360", "surround view", "around view", "multi-view", "multiview", "bird"],
    label: "360° / Surround View",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Multi-View Camera System"],
    searchTerms: ["surround view calibration", "multi-view camera calibration", "around view monitor"],
    docHint: "Requires calibration mat under each camera (front/rear/L/R).",
  },
  {
    match: ["rear camera", "backup camera", "back-up camera", "reverse camera"],
    label: "Rear / Backup Camera",
    path: ["Repair Info", "Articles", "Body & Frame", "Rear View Camera"],
    searchTerms: ["rear camera calibration", "backup camera initialization"],
    docHint: "May be under Body & Frame instead of ADAS — also check Electrical → Camera.",
  },
  {
    match: ["cross traffic", "rcta"],
    label: "Rear Cross Traffic Alert",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Cross Traffic Monitor"],
    searchTerms: ["RCTA calibration", "cross traffic alert", "rear corner sensor"],
    docHint: "Often part of the BSM rear radar calibration procedure.",
  },
  {
    match: ["park assist", "parking sensor", "parking assist", "ultrasonic"],
    label: "Park Assist Sensors",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Parking Assist System"],
    searchTerms: ["park assist calibration", "ultrasonic sensor learn", "parking sensor initialization"],
    docHint: "Some makers require dealer scan tool — verify in ALLDATA.",
  },
  {
    match: ["steering angle", "sas", "yaw", "yaw sensor"],
    label: "Steering Angle Sensor (SAS)",
    path: ["Repair Info", "Articles", "Steering", "Steering Angle Sensor"],
    searchTerms: ["steering angle sensor calibration", "SAS zero point", "yaw rate sensor reset"],
    docHint: 'Look for "Zero Point" or "Calibration" — required after alignment or airbag service.',
  },
  {
    match: ["wheel alignment", "alignment spec"],
    label: "Wheel Alignment",
    path: ["Repair Info", "Articles", "Steering and Suspension", "Wheel Alignment Specifications"],
    searchTerms: ["alignment specifications", "toe", "camber", "caster"],
    docHint: "Pull the spec chart (toe/camber/caster) — attach as proof of procedure.",
  },
  {
    match: ["headlight", "headlamp", "auto-leveling", "adaptive headlamp"],
    label: "Headlight Aim / Adaptive Headlamp",
    path: ["Repair Info", "Articles", "Lighting Systems", "Headlight Aiming"],
    searchTerms: ["headlight aiming", "adaptive headlamp calibration", "AFS calibration"],
    docHint: "Adaptive (AFS) systems require scan tool initialization in addition to manual aim.",
  },
  {
    match: ["night vision", "thermal"],
    label: "Night Vision",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance", "Night Vision System"],
    searchTerms: ["night vision calibration", "thermal camera"],
    docHint: "Rare — usually luxury vehicles. Verify camera type before searching.",
  },
  {
    match: ["suspension", "ride height", "level sensor", "air ride"],
    label: "Ride Height / Suspension Reset",
    path: ["Repair Info", "Articles", "Suspension", "Ride Height Sensor Calibration"],
    searchTerms: ["ride height calibration", "level sensor calibration", "air suspension reset"],
    docHint: "Required after replacement of level sensors or air-suspension components.",
  },
  {
    match: ["brake bleed", "brake fluid"],
    label: "Brake Bleed",
    path: ["Repair Info", "Articles", "Brakes", "Brake Bleeding Procedure"],
    searchTerms: ["brake bleeding procedure", "ABS bleed sequence"],
    docHint: "Pull the bleed sequence diagram — ABS modules often need scan-tool assisted bleed.",
  },
  {
    match: ["tpms", "tire pressure"],
    label: "TPMS Relearn",
    path: ["Repair Info", "Articles", "Wheels & Tires", "TPMS Relearn Procedure"],
    searchTerms: ["TPMS relearn", "tire pressure sensor reset"],
    docHint: "Procedure varies (auto-learn, OBD, magnet) — confirm trigger type.",
  },
  {
    match: ["pre-scan", "pre scan", "post-scan", "post scan", "diagnostic scan"],
    label: "Pre / Post Scan",
    path: ["(Not in ALLDATA)", "Use OEM / Mitchell / I-CAR RTS instead"],
    searchTerms: ["I-CAR position statement scan", "OEM scan requirement"],
    docHint: 'ALLDATA does not provide scan reports. Search I-CAR RTS for "Pre/Post Repair Scan" position statements.',
  },
];

function getAllDataPath(opName) {
  const name = (opName || "").toLowerCase();
  const found = ALLDATA_PATH_RULES.find((r) => r.match.some((k) => name.includes(k)));
  if (found) return found;
  // Fallback for any other ADAS op
  return {
    label: "Generic ADAS",
    path: ["Repair Info", "Articles", "ADAS / Driver Assistance"],
    searchTerms: [opName || "calibration", "initialization"],
    docHint: 'Use the Search box at the top: type the operation name + "calibration" or "initialization".',
  };
}

function AllDataPathHelper({ opName, vehicle, compact = false }) {
  const [open, setOpen] = useState(false);
  const hint = getAllDataPath(opName);
  const vehicleStr = `${vehicle?.year || ""} ${vehicle?.make || ""} ${vehicle?.model || ""}`.trim();

  const copyTerm = async (term) => {
    try {
      await navigator.clipboard.writeText(term);
      toast.success(`Copied: ${term}`);
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:bg-amber-500/10 rounded-md transition-colors"
        data-testid={`alldata-helper-toggle-${opName?.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-amber-300">
          <Lightbulb className="w-3 h-3" />
          Where to find this in ALLDATA
        </span>
        <ChevronRight
          className={`w-3 h-3 text-amber-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="px-2.5 pb-2.5 pt-1 space-y-2">
          {/* Step 1: select vehicle */}
          <div className="text-[11px] text-slate-300 leading-relaxed">
            <span className="text-amber-400 font-bold">1.</span> In ALLDATA, select the vehicle:{" "}
            <span className="font-mono bg-slate-900/60 px-1.5 py-0.5 rounded text-slate-200">
              {vehicleStr || "Year Make Model"}
            </span>
          </div>

          {/* Step 2: navigation breadcrumb */}
          <div className="text-[11px] text-slate-300 leading-relaxed">
            <span className="text-amber-400 font-bold">2.</span> Click through:
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {hint.path.map((seg, i) => (
                <React.Fragment key={i}>
                  <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${
                    seg.startsWith("(")
                      ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                      : "bg-slate-800 text-slate-200 border border-slate-700"
                  }`}>
                    {seg}
                  </span>
                  {i < hint.path.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-amber-500/60" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step 3: search terms (click to copy) */}
          {hint.searchTerms?.length > 0 && (
            <div className="text-[11px] text-slate-300 leading-relaxed">
              <span className="text-amber-400 font-bold">3.</span> Or use the top search box — try one of these
              <span className="text-slate-500"> (click to copy)</span>:
              <div className="mt-1 flex flex-wrap gap-1">
                {hint.searchTerms.map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => copyTerm(t)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-[10px] font-mono text-amber-200"
                    data-testid={`alldata-search-term-${i}`}
                  >
                    {t} <Copy className="w-2.5 h-2.5 opacity-60" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Doc hint */}
          {hint.docHint && (
            <div className="rounded border border-amber-500/20 bg-slate-900/40 p-2 text-[10px] text-amber-100/80 leading-relaxed">
              <span className="font-bold uppercase tracking-wider text-amber-300">Tip:</span>{" "}
              {hint.docHint}
            </div>
          )}

          {/* Step 4 reminder */}
          {!compact && (
            <div className="text-[11px] text-emerald-300/90 leading-relaxed border-t border-amber-500/20 pt-2">
              <span className="text-emerald-400 font-bold">4.</span> When you find the procedure, copy the URL from your browser address bar and click{" "}
              <span className="font-bold">Attach</span> on this card — we&apos;ll remember it for future {vehicle?.make} {vehicle?.model} jobs.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CalibrationReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addingSection, setAddingSection] = useState(null);
  const [vinDialogOpen, setVinDialogOpen] = useState(false);
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  const [prescanOpen, setPrescanOpen] = useState(false);

  const load = React.useCallback(() => {
    api.get(`/calibration-reports/${id}`)
      .then((r) => setDoc(r.data))
      .catch(() => toast.error("Report not found"))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const updateToggles = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch(`/calibration-reports/${id}`, patch);
      setDoc(r.data);
      toast.success("Report recalculated");
    } finally { setBusy(false); }
  };

  const convertToJob = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/calibration-reports/${id}/convert-to-job`);
      toast.success("Work Order created");
      navigate(`/jobs/${r.data.job_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create work order");
    } finally { setBusy(false); }
  };

  const createInvoice = async (invType) => {
    setBusy(true);
    try {
      // Ensure a job exists (work order) — otherwise the invoice generator needs one
      let jobId = doc.job_id;
      if (!jobId) {
        const j = await api.post(`/calibration-reports/${id}/convert-to-job`);
        jobId = j.data.job_id;
        toast.message("Work Order created automatically");
      }
      const inv = await api.post(`/invoices/generate-from-job/${jobId}?type=${invType}`);
      toast.success(`Invoice ${inv.data.invoice_number} created`);
      navigate(`/invoices/${inv.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally { setBusy(false); }
  };

  const removeCard = async (sectionIdx, cardIdx) => {
    const next = JSON.parse(JSON.stringify(doc.report));
    next.sections[sectionIdx].cards.splice(cardIdx, 1);
    next.total_projected_price = next.sections.reduce(
      (s, sec) => s + sec.cards.reduce((c, card) => c + (card.price - (card.discount || 0)), 0), 0
    );
    setDoc({ ...doc, report: next });
    await api.patch(`/calibration-reports/${id}`, { report: next });
    toast.success("Operation removed");
  };

  const saveCardEdit = async (sectionIdx, cardIdx, patch) => {
    const next = JSON.parse(JSON.stringify(doc.report));
    Object.assign(next.sections[sectionIdx].cards[cardIdx], patch);
    next.total_projected_price = next.sections.reduce(
      (s, sec) => s + sec.cards.reduce((c, card) => c + (card.price - (card.discount || 0)), 0), 0
    );
    setDoc({ ...doc, report: next });
    await api.patch(`/calibration-reports/${id}`, { report: next });
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/calibration-reports/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `calibration-report-${(doc.report.vehicle?.vin || "TL").slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading report…
      </div>
    );
  }
  if (!doc) return null;

  const r = doc.report;
  const ext = doc.extracted;
  const allCards = r.sections.flatMap((s) => s.cards);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="calibration-report-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2">
          <button
            onClick={() => navigate("/calibration-reports")}
            className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold hover:text-slate-300 flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Calibration Reports
          </button>
          <h1 className="font-display text-4xl font-semibold tracking-tight" data-testid="report-vehicle-title">
            {r.vehicle.label || "Calibration Report"}
          </h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-400 items-center">
            {ext.customer && <span className="flex items-center gap-1.5"><User className="w-3 h-3" /><span className="text-slate-300 font-medium" data-testid="report-customer">{ext.customer}</span></span>}
            {ext.ro_number && <span className="flex items-center gap-1.5"><Hash className="w-3 h-3" />RO: <span className="font-mono text-slate-300">{ext.ro_number}</span></span>}
            {r.vehicle.vin && <span className="flex items-center gap-1.5"><Car className="w-3 h-3" />VIN: <span className="font-mono text-slate-300">{r.vehicle.vin}</span></span>}
            {ext.body_shop && <span className="flex items-center gap-1.5"><Building2 className="w-3 h-3" />{ext.body_shop}</span>}
            {ext.insurance && <span className="flex items-center gap-1.5"><Shield className="w-3 h-3" />{ext.insurance}{ext.claim_number ? ` · #${ext.claim_number}` : ""}</span>}
            {r.claim?.date_of_loss && <span className="flex items-center gap-1.5" data-testid="report-dol"><span className="text-slate-500">DoL:</span> <span className="font-mono text-slate-300">{r.claim.date_of_loss}</span></span>}
            {r.claim?.point_of_impact && <span className="flex items-center gap-1.5" data-testid="report-poi"><span className="text-slate-500">Impact:</span> <span className="text-amber-300">{r.claim.point_of_impact}</span></span>}
            {r.claim?.adjuster && <span className="flex items-center gap-1.5"><span className="text-slate-500">Adjuster:</span> <span className="text-slate-300">{r.claim.adjuster}</span></span>}
            <button
              onClick={() => setCustomerEditOpen(true)}
              className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-300 hover:text-blue-200 border border-blue-500/40 hover:border-blue-400 rounded px-1.5 py-0.5 bg-blue-500/10"
              data-testid="edit-customer-btn"
              title="Editar cliente / body shop / insurance / RO"
            >
              <Pencil className="w-3 h-3" /> Editar Cliente
            </button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="vin-analyze-open-btn"
            onClick={() => setVinDialogOpen(true)}
            disabled={busy}
            variant="outline"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
          >
            <Car className="w-3.5 h-3.5 mr-1.5" />
            Analyze VIN
          </Button>
          <Button
            data-testid="prescan-open-btn"
            onClick={() => setPrescanOpen(true)}
            disabled={busy}
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
          >
            <Wrench className="w-3.5 h-3.5 mr-1.5" />
            Analyze Pre-Scan
          </Button>
          <Button
            data-testid="convert-to-job-btn"
            onClick={convertToJob}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Briefcase className="w-3.5 h-3.5 mr-1.5" />
            {doc.job_id ? "View Work Order" : "Create Work Order"}
          </Button>
          <Button
            data-testid="invoice-bs-btn"
            onClick={() => createInvoice("body_shop")}
            disabled={busy}
            variant="outline"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          >
            <Building2 className="w-3.5 h-3.5 mr-1.5" />
            Invoice Body Shop
          </Button>
          <Button
            data-testid="invoice-ins-btn"
            onClick={() => createInvoice("insurance")}
            disabled={busy}
            variant="outline"
            className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
          >
            <Shield className="w-3.5 h-3.5 mr-1.5" />
            Invoice Insurance
          </Button>
          <Button variant="outline" className="border-slate-700" onClick={downloadPdf} data-testid="download-pdf-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Vehicle status toggles */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Vehicle Status</div>
          {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between rounded-lg border border-slate-800 p-3">
            <div>
              <Label className="text-sm">Collision Damage</Label>
              <div className="text-[10px] text-slate-500 mt-0.5">Enables full safety inspection ops</div>
            </div>
            <Switch
              checked={r.vehicle_status?.collision_damage ?? true}
              onCheckedChange={(v) => updateToggles({ collision_damage: v })}
              data-testid="report-toggle-collision"
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-800 p-3">
            <div>
              <Label className="text-sm">Post Wheel Alignment</Label>
              <div className="text-[10px] text-slate-500 mt-0.5">Adds SAS reset + alignment check</div>
            </div>
            <Switch
              checked={r.vehicle_status?.post_wheel_alignment ?? true}
              onCheckedChange={(v) => updateToggles({ post_wheel_alignment: v })}
              data-testid="report-toggle-alignment"
            />
          </div>
        </div>
      </div>

      {/* Summary table */}
      {r.summary_table?.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden" data-testid="report-summary-table">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Operations Summary</div>
              <div className="font-display text-lg font-medium mt-0.5">{allCards.length} operation{allCards.length !== 1 ? "s" : ""} required</div>
            </div>
            <Badge className="bg-slate-800/60 border border-slate-700 text-slate-300">
              Total: <span className="ml-1.5 font-mono font-bold text-emerald-300">${r.total_projected_price.toFixed(2)}</span>
            </Badge>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800/60 bg-slate-900/30">
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-2.5">Category</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-2.5">Status</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-2.5">Operation</th>
                <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-2.5">Type</th>
              </tr>
            </thead>
            <tbody>
              {r.summary_table.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/40 last:border-0">
                  <td className="px-5 py-2.5 text-xs">
                    <Badge className={`${SECTION_ACCENTS[row.system]?.border || "border-slate-700"} bg-transparent ${SECTION_ACCENTS[row.system]?.text || "text-slate-400"} text-[10px] uppercase`}>
                      {row.system}
                    </Badge>
                  </td>
                  <td className="px-5 py-2.5 text-xs">
                    <span className={row.status === "Recommended" ? "text-amber-300" : "text-rose-300"}>{row.status}</span>
                  </td>
                  <td className="px-5 py-2.5 text-sm text-slate-200">{row.operation}</td>
                  <td className="px-5 py-2.5 text-xs text-slate-400 capitalize">{row.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sections */}
      {r.sections.map((section, sIdxRender) => {
        if (section.cards.length === 0) return null;
        const accent = SECTION_ACCENTS[section.category] || SECTION_ACCENTS.ADAS;
        const sIdx = r.sections.findIndex((s) => s.category === section.category);
        return (
          <div key={section.category || `section-${sIdxRender}`} data-testid={`section-${(section.category || "adas").replace(/ /g, "-").toLowerCase()}`} className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className={`h-6 w-1 rounded-full ${accent.bar}`} />
                <h2 className="font-display text-2xl font-medium tracking-tight">{section.label}</h2>
                <Badge className="bg-slate-800/60 border border-slate-700 text-slate-400 ml-1">{section.cards.length}</Badge>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-700 text-slate-300 hover:border-blue-500/40 hover:text-blue-300"
                data-testid={`add-op-${(section.category || "adas").replace(/ /g, "-").toLowerCase()}`}
                onClick={() => setAddingSection(section.category)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Operation
              </Button>
            </div>
            <div className="space-y-3">
              {section.cards.map((c, cIdx) => (
                <OperationCard
                  key={c.id + cIdx}
                  card={c}
                  number={cIdx + 1}
                  accent={accent}
                  vehicle={r.vehicle}
                  reportId={id}
                  onRemove={() => removeCard(sIdx, cIdx)}
                  onSave={(patch) => saveCardEdit(sIdx, cIdx, patch)}
                  onLinksChanged={(newDoc) => setDoc(newDoc)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Add Operation dialog */}
      <AddOperationDialog
        open={!!addingSection}
        category={addingSection}
        reportId={id}
        onClose={() => setAddingSection(null)}
        onAdded={(newDoc) => { setDoc(newDoc); setAddingSection(null); toast.success("Operation added"); }}
      />

      {/* VIN Analyze dialog */}
      <VinAnalyzeDialog
        open={vinDialogOpen}
        onClose={() => setVinDialogOpen(false)}
        existingReportId={id}
        onDone={async () => {
          setVinDialogOpen(false);
          try {
            const r = await api.get(`/calibration-reports/${id}`);
            setDoc(r.data);
          } catch (e) { /* best-effort */ }
        }}
      />

      {/* Edit Customer dialog */}
      <EditCustomerDialog
        open={customerEditOpen}
        onClose={() => setCustomerEditOpen(false)}
        reportId={id}
        current={ext}
        onSaved={(newDoc) => {
          setDoc(newDoc);
          setCustomerEditOpen(false);
          toast.success("Cliente atualizado");
        }}
      />

      {/* Pre-Scan Analyzer dialog */}
      <PreScanDialog
        open={prescanOpen}
        onClose={() => setPrescanOpen(false)}
        reportId={id}
        jobId={doc?.job_id}
        onCommitted={async () => {
          setPrescanOpen(false);
          try {
            const r = await api.get(`/calibration-reports/${id}`);
            setDoc(r.data);
            toast.success("Operações adicionadas ao Report");
          } catch { /* noop */ }
        }}
      />

      {/* Total */}
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300">Total Projected Price</div>
          <div className="text-xs text-slate-400 mt-1">All required calibrations, scans and inspections</div>
        </div>
        <div className="font-display text-4xl font-bold text-emerald-300 font-mono" data-testid="report-total">
          ${r.total_projected_price.toFixed(2)}
        </div>
      </div>

      {/* Suppressed operations — transparency on what the rule engine ruled out */}
      {r.suppressed_operations?.length > 0 && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4" data-testid="suppressed-operations">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
            Operations Not Required (Trim Verification)
          </div>
          <div className="text-[11px] text-slate-400 leading-relaxed mb-2">
            The rule engine evaluated and excluded these operations based on the vehicle&apos;s trim configuration:
          </div>
          <ul className="space-y-1">
            {r.suppressed_operations.map((s, i) => (
              <li key={i} className="text-xs text-slate-400">
                <span className="text-slate-300 font-medium">{s.name}</span>
                <span className="text-slate-500"> — {s.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">Disclaimer</div>
        <p className="text-[11px] text-slate-500 leading-relaxed">{r.disclaimer}</p>
      </div>
    </div>
  );
}

function OperationCard({ card, number, accent, vehicle, reportId, onRemove, onSave, onLinksChanged }) {
  const [editing, setEditing] = useState(false);
  const [priceVal, setPriceVal] = useState(String(card.price));
  const [discountVal, setDiscountVal] = useState(String(card.discount || 0));
  const [attachOpen, setAttachOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [enrichOpen, setEnrichOpen] = useState(false);

  const isManual = card.source === "manual";

  const allDataUrl = buildAllDataSearchUrl(vehicle, card.name);
  const googleUrl = buildGoogleOemUrl(vehicle, card.name);

  const removeLink = async (url) => {
    if (!window.confirm("Remove this procedure link from the card?")) return;
    try {
      await api.delete(`/calibration-reports/${reportId}/cards/${card.id}/links`, { params: { url } });
      // Reload report to refresh links
      const r = await api.get(`/calibration-reports/${reportId}`);
      onLinksChanged?.(r.data);
      toast.success("Link removed");
    } catch (e) {
      toast.error("Could not remove link");
    }
  };

  const startEdit = () => {
    setPriceVal(String(card.price));
    setDiscountVal(String(card.discount || 0));
    setEditing(true);
  };
  const commit = async () => {
    const p = parseFloat(priceVal) || 0;
    const d = parseFloat(discountVal) || 0;
    await onSave({ price: p, discount: d });
    setEditing(false);
    toast.success("Price updated");
  };

  return (
    <div className={`rounded-xl border ${accent.border} bg-[#0B1120] p-5`} data-testid={`op-card-${card.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-baseline gap-2">
          <span className={`font-mono text-sm ${accent.text}`}>{number}.</span>
          <h3 className="font-display text-lg font-semibold">{card.name}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Confidence badge — REEV-style */}
          {(() => {
            const conf = card.confidence ?? 0;
            const tone = conf >= 90
              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
              : conf >= 70
              ? "bg-amber-500/15 border-amber-500/40 text-amber-300"
              : "bg-rose-500/15 border-rose-500/40 text-rose-300";
            const label = conf >= 90 ? "VERIFIED" : conf >= 70 ? "INFERRED" : "REVIEW";
            return (
              <Badge
                className={`${tone} border text-[10px] uppercase tracking-wide`}
                data-testid={`confidence-badge-${card.id}`}
                title={`Confidence score: ${conf}%`}
              >
                {label} · {conf}%
              </Badge>
            );
          })()}
          <Badge
            className={
              isManual
                ? "bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-wide"
                : "bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-[10px] uppercase tracking-wide"
            }
            data-testid={`source-badge-${card.id}`}
          >
            {isManual ? (<><Wrench className="w-2.5 h-2.5 mr-1 inline" />Manual</>) : (<><Sparkles className="w-2.5 h-2.5 mr-1 inline" />Auto-detected</>)}
          </Badge>
          <Badge
            className={
              card.status === "Recommended"
                ? "bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-wide"
                : "bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[10px] uppercase tracking-wide"
            }
            data-testid={`status-badge-${card.id}`}
          >
            {card.status || "Required"}
          </Badge>
          <button
            onClick={onRemove}
            className="text-slate-600 hover:text-rose-400"
            data-testid={`remove-op-${card.id}`}
            aria-label="Remove operation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <CardField label="Procedure" value={card.procedure} accent={accent.text} />
          <CardField label="Tools" items={card.tools} />
          {card.triggers_short?.length > 0 && <CardField label="Triggers" items={card.triggers_short} />}
          <CardField label="Systems" items={card.systems} />
          <CardField label="Locations" items={card.locations} />
        </div>
        <div className="space-y-3">
          {card.details?.intro && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">Details</div>
              <p className="text-xs text-slate-300 leading-relaxed">{card.details.intro}</p>
              {card.details.bullets?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {card.details.bullets.map((b, i) => (
                    <li key={i} className="text-xs text-slate-400 flex gap-2">
                      <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-600" />{b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">OEM Procedures (ALLDATA Bridge)</div>
              <div className="flex items-center gap-1">
                <a
                  href={allDataUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-300 hover:text-blue-200 border border-blue-500/30 hover:border-blue-400 rounded px-1.5 py-0.5"
                  data-testid={`alldata-search-${card.id}`}
                  title="Search ALLDATA for OEM procedure"
                >
                  <Search className="w-3 h-3" /> ALLDATA
                </a>
                <a
                  href={`/biblioteca?vin=${encodeURIComponent(vehicle?.vin || "")}&job=${encodeURIComponent(reportId || "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-cyan-300 hover:text-cyan-200 border border-cyan-500/30 hover:border-cyan-400 rounded px-1.5 py-0.5"
                  data-testid={`biblioteca-${card.id}`}
                  title="Consultar Biblioteca TrueLine"
                >
                  <BookOpen className="w-3 h-3" /> Biblioteca
                </a>
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-200 border border-slate-700 hover:border-slate-500 rounded px-1.5 py-0.5"
                  data-testid={`google-search-${card.id}`}
                  title="Web search OEM procedure"
                >
                  <BookOpen className="w-3 h-3" /> OEM
                </a>
                <button
                  onClick={() => setAttachOpen(true)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-400 rounded px-1.5 py-0.5"
                  data-testid={`attach-procedure-${card.id}`}
                  title="Attach captured procedure URL/PDF"
                >
                  <Link2 className="w-3 h-3" /> Attach
                </button>
                <button
                  onClick={() => setCaptureOpen(true)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 hover:text-amber-200 border border-amber-500/40 hover:border-amber-400 rounded px-1.5 py-0.5 bg-amber-500/10"
                  data-testid={`auto-capture-${card.id}`}
                  title="One-click capture from ALLDATA via bookmarklet"
                >
                  <Target className="w-3 h-3" /> Auto-Capture
                </button>
                <button
                  onClick={() => setEnrichOpen(true)}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fuchsia-300 hover:text-fuchsia-200 border border-fuchsia-500/40 hover:border-fuchsia-400 rounded px-1.5 py-0.5 bg-fuchsia-500/10"
                  data-testid={`enrich-alldata-${card.id}`}
                  title="Paste ALLDATA procedure text → AI extracts cal_type, target P/N, equipment, pre-conditions"
                >
                  <Sparkles className="w-3 h-3" /> Enrich AI
                </button>
              </div>
            </div>
            {card.links?.length > 0 ? (
              <div className="space-y-1.5">
                {/* REEV-style colored chips */}
                <div className="flex flex-wrap gap-1.5">
                  {card.links.map((l, i) => {
                    const src = (l.source || "").toUpperCase();
                    const tone = src === "ALLDATA"
                      ? "bg-amber-900/40 border-amber-500/60 text-amber-200 hover:bg-amber-500/20"
                      : src === "OEM"
                      ? "bg-blue-900/40 border-blue-500/60 text-blue-200 hover:bg-blue-500/20"
                      : src === "I-CAR" || src === "ICAR"
                      ? "bg-emerald-900/40 border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/20"
                      : src === "MITCHELL"
                      ? "bg-violet-900/40 border-violet-500/60 text-violet-200 hover:bg-violet-500/20"
                      : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700";
                    return (
                      <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider transition ${tone}`}
                        data-testid={`op-chip-${card.id}-${i}`}
                        title={l.label || l.title || ""}
                      >
                        {src} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    );
                  })}
                </div>
                {/* Full URL list with remove buttons */}
                {card.links.map((l, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/40 hover:border-blue-500/50 hover:bg-blue-500/5 px-2.5 py-1.5 text-xs group"
                    data-testid={`op-link-${card.id}-${i}`}
                  >
                    <a href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-slate-200 flex-1 min-w-0">
                      <FileCheck className="w-3 h-3 text-blue-400 shrink-0" />
                      <span className="truncate">{l.label || l.title}</span>
                    </a>
                    <div className="flex items-center gap-1.5 ml-2 shrink-0">
                      <Badge className="bg-slate-800 border border-slate-700 text-slate-400 text-[9px]">{l.source}</Badge>
                      <a href={l.url} target="_blank" rel="noreferrer"><ExternalLink className="w-3 h-3 text-slate-500 hover:text-blue-300" /></a>
                      <button
                        onClick={() => removeLink(l.url)}
                        className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-link-${card.id}-${i}`}
                        aria-label="Remove link"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-slate-500 italic leading-relaxed">
                No OEM procedure saved yet. Click <span className="text-blue-300 font-medium">ALLDATA</span> to search the manual, then <span className="text-emerald-300 font-medium">Attach</span> the URL — the system will remember it for future {vehicle?.make} {vehicle?.model} jobs.
              </div>
            )}
            {/* ALLDATA Path Helper — always visible (collapsed) to guide employees */}
            <div className="mt-2">
              <AllDataPathHelper opName={card.name} vehicle={vehicle} />
            </div>
          </div>

          <AttachProcedureDialog
            key={`attach-${card.id}-${attachOpen ? "o" : "c"}`}
            open={attachOpen}
            onClose={() => setAttachOpen(false)}
            reportId={reportId}
            opId={card.id}
            opName={card.name}
            vehicle={vehicle}
            allDataUrl={allDataUrl}
            onSaved={async (newDoc) => {
              setAttachOpen(false);
              if (newDoc) {
                onLinksChanged?.(newDoc);
              } else {
                try {
                  const r = await api.get(`/calibration-reports/${reportId}`);
                  onLinksChanged?.(r.data);
                } catch (e) {
                  // best-effort refresh
                }
              }
            }}
          />

          <AutoCaptureDialog
            key={`cap-${card.id}-${captureOpen ? "o" : "c"}`}
            open={captureOpen}
            onClose={() => setCaptureOpen(false)}
            reportId={reportId}
            cardId={card.id}
            cardName={card.name}
            vehicle={vehicle}
            allDataUrl={allDataUrl}
            onCaptured={async () => {
              setCaptureOpen(false);
              try {
                const r = await api.get(`/calibration-reports/${reportId}`);
                onLinksChanged?.(r.data);
              } catch (e) { /* best-effort */ }
            }}
          />

          <AllDataEnrichDialog
            key={`enrich-${card.id}-${enrichOpen ? "o" : "c"}`}
            open={enrichOpen}
            onClose={() => setEnrichOpen(false)}
            reportId={reportId}
            cardId={card.id}
            cardName={card.name}
            vehicle={vehicle}
            allDataUrl={allDataUrl}
            onEnriched={async () => {
              setEnrichOpen(false);
              try {
                const r = await api.get(`/calibration-reports/${reportId}`);
                onLinksChanged?.(r.data);
              } catch (e) { /* best-effort */ }
            }}
          />

          {/* Enrichment display — shows extracted structured data from ALLDATA */}
          {card.enrichment && card.enrichment.cal_type && card.enrichment.cal_type !== "unknown" && (
            <EnrichmentPanel enrichment={card.enrichment} />
          )}

          {/* Pricing with inline edit */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Pricing</div>
              {!editing ? (
                <button
                  onClick={startEdit}
                  className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-blue-300 flex items-center gap-1"
                  data-testid={`edit-price-${card.id}`}
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              ) : (
                <div className="flex gap-1">
                  <button
                    onClick={commit}
                    className="text-[10px] uppercase tracking-wider text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                    data-testid={`save-price-${card.id}`}
                  >
                    <Check className="w-3 h-3" /> Save
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-rose-300 flex items-center gap-1 ml-2"
                  >
                    <X className="w-3 h-3" /> Cancel
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Price</div>
                {editing ? (
                  <Input
                    type="number" step="0.01"
                    value={priceVal}
                    onChange={(e) => setPriceVal(e.target.value)}
                    className="h-7 mt-1 bg-[#030712] border-slate-700 text-center font-mono text-sm"
                    data-testid={`price-input-${card.id}`}
                  />
                ) : (
                  <div className="font-mono text-sm text-slate-200 mt-1">${card.price.toFixed(2)}</div>
                )}
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Discount</div>
                {editing ? (
                  <Input
                    type="number" step="0.01"
                    value={discountVal}
                    onChange={(e) => setDiscountVal(e.target.value)}
                    className="h-7 mt-1 bg-[#030712] border-slate-700 text-center font-mono text-sm"
                    data-testid={`discount-input-${card.id}`}
                  />
                ) : (
                  <div className="font-mono text-sm text-slate-400 mt-1">${(card.discount || 0).toFixed(2)}</div>
                )}
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider text-emerald-400">Final</div>
                <div className="font-mono text-sm font-bold text-emerald-300 mt-1">
                  ${(
                    (editing ? parseFloat(priceVal) || 0 : card.price) -
                    (editing ? parseFloat(discountVal) || 0 : card.discount || 0)
                  ).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Add Operation Dialog — picks from OPERATION_LIBRARY or fully custom
// ─────────────────────────────────────────────────────────────────
function AddOperationDialog({ open, category, reportId, onClose, onAdded }) {
  const [library, setLibrary] = useState([]);
  const [picked, setPicked] = useState(null);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [tab, setTab] = useState("library"); // library | custom
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && library.length === 0) {
      api.get("/calibration-reports/_operations/library").then((r) => setLibrary(r.data));
    }
  }, [open, library.length]);

  if (!open) return null;

  const filtered = library.filter((op) => !category || op.category === category);

  const save = async () => {
    setSaving(true);
    try {
      const body = tab === "library" && picked
        ? { op_id: picked.id }
        : { name: customName, category: category || "ADAS", price: parseFloat(customPrice) || 0 };
      const r = await api.post(`/calibration-reports/${reportId}/cards`, body);
      onAdded(r.data);
      setPicked(null); setCustomName(""); setCustomPrice("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800">
        <DialogHeader>
          <DialogTitle>Add Operation · {category}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Pick from the calibration library or define a custom operation.
          </DialogDescription>
        </DialogHeader>
        <div className="flex border-b border-slate-800 mb-3">
          <button
            onClick={() => setTab("library")}
            className={`px-3 py-2 text-xs uppercase tracking-wider font-bold ${tab === "library" ? "border-b-2 border-blue-500 text-blue-300" : "text-slate-400"}`}
            data-testid="add-op-tab-library"
          >
            From Library
          </button>
          <button
            onClick={() => setTab("custom")}
            className={`px-3 py-2 text-xs uppercase tracking-wider font-bold ${tab === "custom" ? "border-b-2 border-blue-500 text-blue-300" : "text-slate-400"}`}
            data-testid="add-op-tab-custom"
          >
            Custom
          </button>
        </div>

        {tab === "library" ? (
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {filtered.length === 0 && <div className="text-center text-slate-500 text-sm py-6">No operations in this category</div>}
            {filtered.map((op) => (
              <button
                key={op.id}
                onClick={() => setPicked(op)}
                data-testid={`add-op-lib-${op.id}`}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  picked?.id === op.id ? "border-blue-500/60 bg-blue-500/10" : "border-slate-800 hover:border-slate-700"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-200">{op.name}</div>
                  <div className="font-mono text-xs text-emerald-300">${op.fee.toFixed(2)}</div>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">{op.procedure}</div>
                <div className="text-[10px] text-slate-400 mt-1 truncate">Tools: {op.tools.slice(0, 3).join(", ")}</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Operation Name</Label>
              <Input
                value={customName} onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Rear Backup Camera Calibration"
                className="mt-1.5 bg-[#030712] border-slate-800"
                data-testid="custom-op-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Price ($)</Label>
              <Input
                type="number" step="0.01"
                value={customPrice} onChange={(e) => setCustomPrice(e.target.value)}
                placeholder="e.g. 350.00"
                className="mt-1.5 bg-[#030712] border-slate-800 font-mono"
                data-testid="custom-op-price"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || (tab === "library" ? !picked : !customName)}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="add-op-confirm-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
            Add Operation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CardField({ label, value, items, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{label}</div>
      {value ? (
        <div className={`text-sm ${accent || "text-slate-200"}`}>{value}</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items?.map((it, i) => (
            <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-slate-800/60 border border-slate-700 text-slate-300">
              {it}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// ALLDATA Bridge — Attach Procedure Dialog
// User opens ALLDATA in a new tab via the "ALLDATA" button, then
// pastes the URL of the procedure they found (or uploads the PDF).
// Saves to master_position_statements for future auto-population.
// ──────────────────────────────────────────────────────────────
function AttachProcedureDialog({ open, onClose, reportId, opId, opName, vehicle, allDataUrl, onSaved }) {
  const initialYear = parseInt(vehicle?.year, 10);
  const initialTitle = `${vehicle?.year || ""} ${vehicle?.make || ""} ${vehicle?.model || ""} — ${opName}`.trim();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState(initialTitle);
  const [source, setSource] = useState("alldata");
  const [yearMin, setYearMin] = useState(initialYear ? String(initialYear - 1) : "");
  const [yearMax, setYearMax] = useState(initialYear ? String(initialYear + 1) : "");
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!url.trim()) {
      toast.error("Please paste the procedure URL or PDF link");
      return;
    }
    setSaving(true);
    try {
      const body = {
        url: url.trim(),
        title: title.trim() || opName,
        source,
        save_to_library: saveToLibrary,
      };
      if (yearMin) body.year_min = parseInt(yearMin, 10);
      if (yearMax) body.year_max = parseInt(yearMax, 10);
      const resp = await api.post(`/calibration-reports/${reportId}/cards/${opId}/attach-procedure`, body);
      toast.success(saveToLibrary
        ? "Procedure attached & saved to library for future jobs"
        : "Procedure attached to this report");
      onSaved?.(resp.data?.report);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to attach procedure");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-[#0B1120] border-slate-800" data-testid="attach-procedure-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-emerald-400" /> Attach OEM Procedure
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Paste the URL of the OEM procedure you found on ALLDATA / I-CAR / manufacturer site.
            We&apos;ll attach it to this card AND remember it for future {vehicle?.make} {vehicle?.model} jobs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-2.5 text-xs flex items-center justify-between">
            <span className="text-slate-300">
              <span className="text-slate-500">Operation:</span> <span className="font-medium">{opName}</span>
            </span>
            <a
              href={allDataUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 text-[11px] uppercase tracking-wider"
            >
              <Search className="w-3 h-3" /> Open ALLDATA Search
            </a>
          </div>

          <AllDataPathHelper opName={opName} vehicle={vehicle} compact />

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Procedure URL or PDF Link *</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://my.alldata.com/repair/..."
              className="mt-1.5 bg-[#030712] border-slate-800 font-mono text-xs"
              data-testid="attach-url-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5 bg-[#030712] border-slate-800 text-xs"
              data-testid="attach-title-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Source</Label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="mt-1.5 w-full h-9 px-2 rounded-md bg-[#030712] border border-slate-800 text-xs text-slate-200"
                data-testid="attach-source-select"
              >
                <option value="alldata">ALLDATA</option>
                <option value="oem">OEM Direct</option>
                <option value="icar">I-CAR RTS</option>
                <option value="mitchell">Mitchell</option>
                <option value="manual">Other</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Year Min</Label>
              <Input
                type="number"
                value={yearMin}
                onChange={(e) => setYearMin(e.target.value)}
                className="mt-1.5 bg-[#030712] border-slate-800 font-mono text-xs"
                data-testid="attach-year-min"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Year Max</Label>
              <Input
                type="number"
                value={yearMax}
                onChange={(e) => setYearMax(e.target.value)}
                className="mt-1.5 bg-[#030712] border-slate-800 font-mono text-xs"
                data-testid="attach-year-max"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer rounded-md border border-slate-800 bg-slate-900/40 p-2.5 hover:border-emerald-500/30">
            <input
              type="checkbox"
              checked={saveToLibrary}
              onChange={(e) => setSaveToLibrary(e.target.checked)}
              className="accent-emerald-500"
              data-testid="attach-save-library"
            />
            <div className="flex-1">
              <div className="text-xs text-slate-200 font-medium">Save to ALLDATA Bridge Library</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                Auto-populate this procedure on future Calibration Reports for matching {vehicle?.make} {vehicle?.model} ({yearMin || "?"}–{yearMax || "?"}) + same operation.
              </div>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || !url.trim()}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="attach-confirm-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
            Attach Procedure
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Auto-Capture Dialog — starts a bridge session, opens ALLDATA in
// a new tab, polls /status until the bookmarklet fires.
// ──────────────────────────────────────────────────────────────
function AutoCaptureDialog({ open, onClose, reportId, cardId, cardName, vehicle, allDataUrl, onCaptured }) {
  const [session, setSession] = useState(null);  // {token, expires_at}
  const [status, setStatus] = useState("idle");  // idle | pending | complete | expired | error
  const [captured, setCaptured] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = React.useRef(null);
  const alldataTabRef = React.useRef(null);

  const stop = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  React.useEffect(() => () => stop(), [stop]);

  const startSession = async () => {
    setStarting(true);
    try {
      const r = await api.post("/alldata-bridge/start-capture", {
        report_id: reportId, card_id: cardId,
      });
      setSession(r.data);
      setStatus("pending");
      // Open ALLDATA in a new tab
      try {
        alldataTabRef.current = window.open(allDataUrl, "_blank");
      } catch (e) { /* popup blocked */ }
      // Begin polling
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/alldata-bridge/status/${r.data.token}`);
          if (s.data.status === "complete") {
            stop();
            setCaptured(s.data);
            setStatus("complete");
            toast.success("Captured from ALLDATA!");
            // Auto-refresh report after 1.5s
            setTimeout(() => onCaptured?.(), 1500);
          } else if (s.data.status === "expired" || s.data.status === "error") {
            stop();
            setStatus(s.data.status);
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start capture session");
    } finally {
      setStarting(false);
    }
  };

  const cancel = () => {
    stop();
    setSession(null);
    setStatus("idle");
    setCaptured(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && cancel()}>
      <DialogContent className="max-w-md bg-[#0B1120] border-slate-800" data-testid="auto-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-4 h-4 text-amber-400" /> Auto-Capture from ALLDATA
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            One click to attach an OEM procedure URL — no copy-paste.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-2.5 text-xs">
            <div className="text-slate-400">Operation</div>
            <div className="text-slate-200 font-medium">{cardName}</div>
            <div className="text-slate-500 text-[11px] mt-1">{vehicle?.year} {vehicle?.make} {vehicle?.model}</div>
          </div>

          {status === "idle" && (
            <>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2 text-xs">
                <div className="text-amber-200 font-medium">Before you start</div>
                <ol className="list-decimal list-inside space-y-1 text-slate-300">
                  <li>You need the &quot;Send to TrueLine&quot; bookmarklet installed.</li>
                  <li>Don&apos;t have it? <a href="/settings/alldata-bridge" target="_blank" rel="noreferrer" className="text-blue-300 underline">Install in 60s →</a></li>
                </ol>
              </div>
              <Button
                onClick={startSession}
                disabled={starting}
                className="w-full bg-amber-600 hover:bg-amber-500"
                data-testid="auto-capture-start-btn"
              >
                {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Target className="w-4 h-4 mr-2" />}
                Start Capture & Open ALLDATA
              </Button>
            </>
          )}

          {status === "pending" && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-center" data-testid="auto-capture-waiting">
              <RefreshCw className="w-7 h-7 text-amber-400 animate-spin mx-auto mb-2" />
              <div className="text-sm font-semibold text-amber-200">Waiting for capture...</div>
              <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                1. In the ALLDATA tab, navigate to the OEM procedure page.<br/>
                2. Click <strong className="text-blue-300">Send to TrueLine</strong> in your bookmarks bar.<br/>
                3. Pick this session in the picker that opens.
              </div>
              <div className="text-[10px] text-slate-600 mt-3 font-mono">
                Token: {session?.token?.slice(0, 8)}… · Expires {session?.expires_at ? new Date(session.expires_at).toLocaleTimeString() : "—"}
              </div>
              {!alldataTabRef.current && (
                <a
                  href={allDataUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-300 hover:text-blue-200 underline"
                >
                  <ExternalLink className="w-3 h-3" /> ALLDATA didn&apos;t open? Click here.
                </a>
              )}
            </div>
          )}

          {status === "complete" && captured && (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-4 text-center" data-testid="auto-capture-complete">
              <Check className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
              <div className="text-sm font-semibold text-emerald-200 mb-1">Captured!</div>
              <div className="text-xs text-slate-200 truncate" title={captured.captured_title}>{captured.captured_title}</div>
              <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 mt-2 text-[10px]">
                {(captured.captured_source || "manual").toUpperCase()}
              </Badge>
              <div className="text-[10px] text-slate-500 mt-3 italic">Refreshing card...</div>
            </div>
          )}

          {(status === "expired" || status === "error") && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-4 text-center">
              <X className="w-7 h-7 text-rose-400 mx-auto mb-2" />
              <div className="text-sm font-semibold text-rose-200 mb-1">
                {status === "expired" ? "Session expired" : "Capture failed"}
              </div>
              <div className="text-[11px] text-slate-400">Try starting a new capture.</div>
              <Button
                onClick={() => { setStatus("idle"); setSession(null); setCaptured(null); }}
                size="sm"
                className="mt-3 bg-slate-700 hover:bg-slate-600"
                data-testid="auto-capture-retry-btn"
              >
                Start over
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="border-slate-700" onClick={cancel} data-testid="auto-capture-cancel-btn">
            {status === "complete" ? "Close" : "Cancel"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────
// Enrichment display panel — shows structured ADAS data extracted by AI
// ─────────────────────────────────────────────────────────────────
function EnrichmentPanel({ enrichment }) {
  const calType = (enrichment.cal_type || "unknown").toLowerCase();
  const calTone = {
    static: "bg-blue-500/15 border-blue-500/40 text-blue-200",
    dynamic: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
    dual: "bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-200",
    unknown: "bg-slate-800 border-slate-700 text-slate-400",
  }[calType] || "bg-slate-800 border-slate-700 text-slate-400";
  const conf = enrichment.confidence ?? 0;
  const confTone = conf >= 90 ? "text-emerald-300" : conf >= 70 ? "text-amber-300" : "text-rose-300";

  return (
    <div
      className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-2"
      data-testid="enrichment-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-fuchsia-300">
            ALLDATA AI Enrichment
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge className={`${calTone} border text-[10px] uppercase tracking-wider font-bold`} data-testid="enrichment-cal-type">
            {calType.toUpperCase()}
          </Badge>
          <span className={`text-[10px] font-mono ${confTone}`} title={enrichment.confidence_reason || ""}>
            {conf}%
          </span>
        </div>
      </div>

      {enrichment.target_part_number && (
        <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px]">
          <div className="text-slate-500">Target P/N:</div>
          <div className="col-span-2 font-mono text-cyan-300" data-testid="enrichment-target-pn">{enrichment.target_part_number}</div>
          {enrichment.target_description && (
            <>
              <div className="text-slate-500">Target:</div>
              <div className="col-span-2 text-slate-300">{enrichment.target_description}</div>
            </>
          )}
        </div>
      )}

      {enrichment.equipment_matched?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">Equipment</div>
          <div className="flex flex-wrap gap-1">
            {enrichment.equipment_matched.map((eq, i) => {
              const isMatched = !!eq.matched_id;
              return (
                <Badge
                  key={i}
                  className={
                    isMatched
                      ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-[10px]"
                      : "bg-slate-800 border border-slate-600 text-slate-300 text-[10px]"
                  }
                  title={isMatched ? `Matched to inventory: ${eq.matched_name} (${eq.match_score}%)` : "Not in inventory"}
                  data-testid={`enrichment-equipment-${i}`}
                >
                  {isMatched && <Check className="w-2.5 h-2.5 mr-1 inline" />}
                  {eq.name}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {enrichment.pre_conditions?.length > 0 && (
        <details className="text-[11px]">
          <summary className="text-slate-400 cursor-pointer hover:text-slate-200">
            Pre-conditions ({enrichment.pre_conditions.length})
          </summary>
          <ul className="mt-1.5 space-y-0.5 pl-3">
            {enrichment.pre_conditions.map((p, i) => (
              <li key={i} className="text-slate-300 flex gap-1.5">
                <ChevronRight className="w-2.5 h-2.5 mt-1 shrink-0 text-slate-600" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {enrichment.post_cal_validation && (
        <div className="text-[11px]">
          <span className="text-slate-500">Post-cal: </span>
          <span className="text-slate-300">{enrichment.post_cal_validation}</span>
        </div>
      )}

      {enrichment.estimated_labor_hours > 0 && (
        <div className="text-[10px] text-slate-500">
          Labor: <span className="font-mono text-slate-300">{enrichment.estimated_labor_hours}h</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// AllData Enrich Dialog — paste procedure text → AI extracts structured data
// ─────────────────────────────────────────────────────────────────
function AllDataEnrichDialog({ open, onClose, reportId, cardId, cardName, vehicle, allDataUrl, onEnriched }) {
  const [pageText, setPageText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceTitle, setSourceTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cachedFound, setCachedFound] = useState(null);
  const [checkedCache, setCheckedCache] = useState(false);

  useEffect(() => {
    if (open && !checkedCache) {
      // Look up cached enrichment for this vehicle+card
      api.post("/alldata-enrich/lookup-cached", { report_id: reportId, card_id: cardId })
        .then((r) => {
          if (r.data?.cached) {
            setCachedFound(r.data);
          }
        })
        .catch(() => {})
        .finally(() => setCheckedCache(true));
    }
    if (!open) {
      setPageText(""); setSourceUrl(""); setSourceTitle(""); setPreview(null);
      setCachedFound(null); setCheckedCache(false);
    }
  }, [open, reportId, cardId, checkedCache]);

  const applyCache = async () => {
    if (!cachedFound?.enrichment) return;
    setBusy(true);
    try {
      await api.post("/alldata-enrich/apply-cached", {
        report_id: reportId,
        card_id: cardId,
        enrichment: cachedFound.enrichment,
      });
      toast.success("Cached enrichment applied");
      onEnriched?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply cached data");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!pageText || pageText.trim().length < 50) {
      toast.error("Paste at least 50 characters of the ALLDATA procedure text");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/alldata-enrich/enrich-card", {
        report_id: reportId,
        card_id: cardId,
        page_text: pageText,
        source_url: sourceUrl || null,
        source_title: sourceTitle || null,
        save_to_library: true,
      });
      setPreview(r.data.enrichment);
      toast.success(`Extracted with ${r.data.enrichment?.confidence}% confidence`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI extraction failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800" data-testid="enrich-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-fuchsia-300">
            <Sparkles className="w-4 h-4" /> Enrich from ALLDATA · {cardName}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Paste the ALLDATA procedure text. Claude Sonnet 5 will extract cal_type (static/dynamic/dual), target P/N, equipment, pre-conditions and post-cal validation.
          </DialogDescription>
        </DialogHeader>

        {cachedFound && !preview && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <div className="text-xs font-semibold text-emerald-200">Cached enrichment found for this vehicle</div>
            </div>
            <div className="text-[11px] text-slate-300">
              <div>Match: <span className="font-mono">{cachedFound.vehicle_match?.make} {cachedFound.vehicle_match?.model} ({cachedFound.vehicle_match?.year_min}–{cachedFound.vehicle_match?.year_max})</span></div>
              <div>Type: <span className="font-mono text-fuchsia-300">{cachedFound.enrichment?.cal_type?.toUpperCase()}</span> · Target: <span className="font-mono text-cyan-300">{cachedFound.enrichment?.target_part_number || "—"}</span></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={applyCache} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500" data-testid="enrich-apply-cache-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Apply cached (instant)</>}
              </Button>
              <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setCachedFound(null)}>
                Re-extract from new text
              </Button>
            </div>
          </div>
        )}

        {!preview && !cachedFound && (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
              <div className="text-[11px] text-amber-200">
                <b>Quick flow:</b> Click <b>Open ALLDATA</b> below → login → find the procedure for <b>{vehicle?.label} · {cardName}</b> →
                select all (Ctrl+A / ⌘+A) → copy (Ctrl+C / ⌘+C) → come back here and paste.
              </div>
              {allDataUrl && (
                <a
                  href={allDataUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded transition"
                  data-testid="enrich-open-alldata-btn"
                >
                  <ExternalLink className="w-3 h-3" /> Open ALLDATA (pre-filled: {vehicle?.year} {vehicle?.make} {vehicle?.model})
                </a>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Source URL (optional)</Label>
                <Input
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://my.alldata.com/..."
                  className="bg-slate-900 border-slate-700 mt-1 text-xs font-mono"
                  data-testid="enrich-source-url"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Source title (optional)</Label>
                <Input
                  value={sourceTitle}
                  onChange={(e) => setSourceTitle(e.target.value)}
                  placeholder="e.g. Front Camera Calibration"
                  className="bg-slate-900 border-slate-700 mt-1 text-xs"
                  data-testid="enrich-source-title"
                />
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                Procedure text (min 50 chars, max 40k)
              </Label>
              <textarea
                value={pageText}
                onChange={(e) => setPageText(e.target.value)}
                rows={12}
                placeholder="Paste the full ALLDATA procedure page here (Ctrl+A → Ctrl+C on ALLDATA, then Ctrl+V here)..."
                className="w-full mt-1 rounded-md bg-slate-900 border border-slate-700 p-2 text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:border-fuchsia-500 focus:outline-none"
                data-testid="enrich-page-text"
              />
              <div className="text-[10px] text-slate-500 mt-1 text-right">
                {pageText.length.toLocaleString()} chars
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={busy || pageText.trim().length < 50}
                className="bg-fuchsia-600 hover:bg-fuchsia-500"
                data-testid="enrich-submit-btn"
              >
                {busy ? <><Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Extracting...</> : <><Sparkles className="w-3 h-3 mr-1.5" /> Extract with AI</>}
              </Button>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <EnrichmentPanel enrichment={preview} />
            <div className="text-[11px] text-slate-400 italic">
              Saved to card and master library. Next {vehicle?.make} {vehicle?.model} in the same year range will auto-suggest this data.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-slate-700" onClick={() => { setPreview(null); setPageText(""); }}>
                Extract another
              </Button>
              <Button onClick={onEnriched} className="bg-emerald-600 hover:bg-emerald-500" data-testid="enrich-done-btn">
                <Check className="w-3 h-3 mr-1.5" /> Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────
// VIN Analyze Dialog — creates a new report or merges into existing one from VIN
// ─────────────────────────────────────────────────────────────────
export function VinAnalyzeDialog({ open, onClose, existingReportId, onDone }) {
  const [vin, setVin] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [existingJobs, setExistingJobs] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [bodyShopName, setBodyShopName] = useState("");
  const [bodyShopId, setBodyShopId] = useState("");
  const [insuranceName, setInsuranceName] = useState("");
  const [roNumber, setRoNumber] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      // Load a unified customer+body-shop picker (same pattern used in NewJobDirect)
      (async () => {
        try {
          const [crm, mc, bs] = await Promise.all([
            api.get("/crm/contacts?type=customer"),
            api.get("/master/customers"),
            api.get("/master/body-shops"),
          ]);
          const items = [];
          const seen = new Set();
          const add = (v) => { if (v && v.id && !seen.has(v.id)) { seen.add(v.id); items.push(v); } };
          (bs.data || []).filter((s) => !s.archived).forEach((s) => add({
            id: s.id, kind: "body_shop", name: s.company_name, phone: s.phone,
          }));
          (crm.data || []).forEach((c) => add({ id: c.id, kind: "customer", name: c.name, phone: c.phone }));
          (mc.data || []).filter((c) => !c.archived).forEach((c) => add({
            id: c.id, kind: "customer",
            name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—",
            phone: c.phone,
          }));
          setContactOptions(items);
        } catch { /* noop */ }
      })();
    }
    if (!open) {
      setVin(""); setPreview(null); setExistingJobs([]);
      setCustomerName(""); setCustomerId(""); setBodyShopName(""); setBodyShopId("");
      setInsuranceName(""); setRoNumber(""); setContactSearch(""); setShowPicker(false);
    }
  }, [open]);

  const runAnalyze = async () => {
    const v = vin.toUpperCase().trim();
    if (!/^[A-HJ-NPR-Z0-9]{11,17}$/.test(v)) {
      toast.error("Invalid VIN — must be 11–17 chars, no I/O/Q");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/estimate-analyzer/from-vin", { vin: v, persist: false });
      setPreview(r.data);
      // Concurrent: look up open Jobs matching this VIN
      try {
        const j = await api.get(`/jobs/by-vin/${v}`);
        // Exclude the one this report already belongs to (if editing existing)
        const jobs = (j.data || []).filter((x) => !existingReportId || x.calibration_report_id !== existingReportId);
        setExistingJobs(jobs);
        // Pre-fill customer if a matching job exists and we haven't set one yet
        if (jobs.length > 0 && !customerName && !bodyShopName) {
          const top = jobs[0];
          if (top.customer_name) setCustomerName(top.customer_name);
        }
      } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "VIN analysis failed");
    } finally { setBusy(false); }
  };

  const commit = async (mode) => {
    setBusy(true);
    try {
      const payload = {
        vin: vin.toUpperCase().trim(),
        customer: customerName || null,
        body_shop: bodyShopName || null,
        insurance: insuranceName || null,
        ro_number: roNumber || null,
      };
      if (mode === "new") {
        const r = await api.post("/estimate-analyzer/from-vin", { ...payload, persist: true });
        toast.success(`Report created with ${r.data.report.sections.reduce((s, sec) => s + sec.cards.length, 0)} operations`);
        onClose?.();
        navigate(`/calibration-reports/${r.data.id}`);
      } else if (mode === "merge" && existingReportId) {
        const r = await api.post(`/calibration-reports/${existingReportId}/merge-vin`, payload);
        toast.success(`Merged: ${r.data.added_count} new operation(s) added`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const attachToExistingJob = async (job) => {
    setBusy(true);
    try {
      // If job already has a report, merge new VIN ops into that report
      if (job.calibration_report_id) {
        await api.post(`/calibration-reports/${job.calibration_report_id}/merge-vin`, {
          vin: vin.toUpperCase().trim(),
        });
        toast.success(`Anexado ao Report existente do Job ${job.id.slice(0, 8)}`);
        onClose?.();
        navigate(`/calibration-reports/${job.calibration_report_id}`);
        return;
      }
      // Else create a fresh report and link it to this Job
      const payload = {
        vin: vin.toUpperCase().trim(),
        customer: customerName || job.customer_name || null,
        body_shop: bodyShopName || null,
        insurance: insuranceName || null,
        ro_number: roNumber || null,
        persist: true,
      };
      const r = await api.post("/estimate-analyzer/from-vin", payload);
      const newReportId = r.data.id;
      // Link report -> job (both directions)
      await api.patch(`/jobs/${job.id}`, { calibration_report_id: newReportId });
      toast.success(`Report criado e anexado ao Job ${job.id.slice(0, 8)}`);
      onClose?.();
      navigate(`/calibration-reports/${newReportId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao anexar");
    } finally { setBusy(false); }
  };

  const pickContact = (c) => {
    if (c.kind === "body_shop") {
      setBodyShopName(c.name); setBodyShopId(c.id);
      setCustomerName(""); setCustomerId("");
    } else {
      setCustomerName(c.name); setCustomerId(c.id);
      setBodyShopName(""); setBodyShopId("");
    }
    setShowPicker(false); setContactSearch("");
    toast.success(`${c.kind === "body_shop" ? "Body Shop" : "Cliente"} selecionado`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#0B1120] border-slate-800" data-testid="vin-analyze-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-300">
            <Car className="w-4 h-4" /> Analyze by VIN
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Decode via NHTSA + trim-feature rules engine → auto-populate ADAS/Alignment/Safety cards. No estimate PDF needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              placeholder="17-digit VIN (e.g. 2HKRW1H59JH522884)"
              className="bg-slate-900 border-slate-700 font-mono text-sm"
              data-testid="vin-analyze-input"
              maxLength={17}
            />
            <Button
              onClick={runAnalyze}
              disabled={busy || vin.length < 11}
              className="bg-cyan-600 hover:bg-cyan-500"
              data-testid="vin-analyze-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1.5" /> Analyze</>}
            </Button>
          </div>

          {preview && existingJobs.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="existing-jobs-panel">
              <div className="flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 text-amber-300" />
                <div className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">
                  {existingJobs.length} Job{existingJobs.length > 1 ? "s" : ""} aberto{existingJobs.length > 1 ? "s" : ""} com esse VIN
                </div>
              </div>
              <div className="text-[11px] text-amber-100/80">
                Quer anexar esse Report a um Job existente em vez de criar um novo?
              </div>
              <div className="space-y-1.5">
                {existingJobs.slice(0, 3).map((j) => (
                  <div key={j.id} className="flex items-center justify-between rounded-md bg-slate-900/60 border border-slate-700 px-2.5 py-1.5" data-testid={`existing-job-${j.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-200 font-medium truncate">
                        {j.customer_name || "sem cliente"}
                        <span className="ml-1.5 text-[10px] font-mono text-slate-500">#{j.id.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <Badge className={`bg-slate-800 border-slate-600 text-slate-300 text-[9px] uppercase`}>
                          {(j.status || "new").replace(/_/g, " ")}
                        </Badge>
                        {j.calibration_report_id && <span className="text-emerald-400">✓ tem Report</span>}
                        <span className="font-mono text-emerald-400">${(j.total_amount || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => attachToExistingJob(j)}
                      disabled={busy}
                      size="sm"
                      className="ml-2 bg-amber-600 hover:bg-amber-500 h-7 text-[11px]"
                      data-testid={`attach-job-${j.id}-btn`}
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      {j.calibration_report_id ? "Merge no Report" : "Anexar"}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 italic pt-1 border-t border-amber-500/20">
                Ou preencha um novo cliente abaixo para criar um Report standalone.
              </div>
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Cliente / Destinatário</div>
                {(customerName || bodyShopName) && (
                  <button
                    onClick={() => { setCustomerName(""); setCustomerId(""); setBodyShopName(""); setBodyShopId(""); }}
                    className="text-[10px] text-slate-400 hover:text-slate-200"
                  >
                    Limpar
                  </button>
                )}
              </div>
              {customerName || bodyShopName ? (
                <div className="rounded-md bg-slate-900/50 border border-slate-700 px-3 py-2 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">{customerName ? "Cliente" : "Body Shop"}</div>
                    <div className="text-sm font-semibold text-slate-100" data-testid="vin-selected-contact">
                      {customerName || bodyShopName}
                    </div>
                  </div>
                  <Badge className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-[10px]">Vinculado</Badge>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => setShowPicker(!showPicker)}
                    className="w-full text-left rounded-md bg-slate-900/50 border border-dashed border-slate-700 hover:border-emerald-500 px-3 py-2.5 text-xs text-slate-400 hover:text-emerald-300 transition-colors"
                    data-testid="vin-pick-contact-btn"
                  >
                    <User className="w-3.5 h-3.5 inline mr-1.5" />
                    {showPicker ? "Fechar seletor" : "Clique para escolher cliente ou body shop"}
                  </button>
                  {showPicker && (
                    <div className="rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-1.5" data-testid="vin-contact-picker">
                      <Input
                        placeholder="Buscar por nome..."
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-xs h-8"
                        autoFocus
                      />
                      <div className="max-h-48 overflow-y-auto space-y-0.5">
                        {contactOptions
                          .filter((c) => !contactSearch || (c.name || "").toLowerCase().includes(contactSearch.toLowerCase()))
                          .slice(0, 30)
                          .map((c) => (
                            <button
                              key={c.id}
                              onClick={() => pickContact(c)}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800 flex items-center justify-between text-xs"
                              data-testid={`vin-contact-opt-${c.id}`}
                            >
                              <div>
                                <div className="text-slate-200">{c.name}</div>
                                {c.phone && <div className="text-[10px] text-slate-500 font-mono">{c.phone}</div>}
                              </div>
                              <Badge className={c.kind === "body_shop"
                                ? "bg-orange-500/15 border border-orange-500/40 text-orange-200 text-[9px]"
                                : "bg-blue-500/15 border border-blue-500/40 text-blue-200 text-[9px]"}
                              >
                                {c.kind === "body_shop" ? "SHOP" : "CLIENT"}
                              </Badge>
                            </button>
                          ))}
                        {contactOptions.length === 0 && (
                          <div className="text-[11px] text-slate-500 italic text-center py-3">Sem contatos cadastrados. Cadastre em Master Data.</div>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500 italic pt-1 border-t border-slate-800">
                        Ou preencha manualmente abaixo (funciona para walk-in):
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Cliente (walk-in)"
                      value={customerName}
                      onChange={(e) => { setCustomerName(e.target.value); setCustomerId(""); }}
                      className="bg-slate-900 border-slate-700 text-xs h-8"
                      data-testid="vin-customer-input"
                    />
                    <Input
                      placeholder="Body Shop (opcional)"
                      value={bodyShopName}
                      onChange={(e) => { setBodyShopName(e.target.value); setBodyShopId(""); }}
                      className="bg-slate-900 border-slate-700 text-xs h-8"
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Insurance (se aplicável)"
                  value={insuranceName}
                  onChange={(e) => setInsuranceName(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-xs h-8"
                  data-testid="vin-insurance-input"
                />
                <Input
                  placeholder="RO / PO # (opcional)"
                  value={roNumber}
                  onChange={(e) => setRoNumber(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-xs h-8"
                  data-testid="vin-ro-input"
                />
              </div>
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Vehicle</div>
                <div className="text-sm font-semibold text-cyan-200" data-testid="vin-preview-vehicle">
                  {preview.report.vehicle.label} <span className="text-slate-400 font-normal">· Trim: {preview.report.vehicle.trim || "—"}</span>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Detected Operations</div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {preview.report.sections.map((s) => s.cards.length > 0 && (
                    <div key={s.category}>
                      <div className="text-[10px] uppercase font-bold text-slate-400 mt-1">{s.label}</div>
                      {s.cards.map((c) => (
                        <div key={c.id} className="flex justify-between text-xs py-0.5" data-testid={`vin-preview-op-${c.id}`}>
                          <div className="text-slate-200">
                            {c.name}{" "}
                            <span className={c.confidence >= 90 ? "text-emerald-400" : c.confidence >= 70 ? "text-amber-400" : "text-rose-400"}>
                              · {c.confidence}%
                            </span>
                          </div>
                          <div className="font-mono text-emerald-300">${c.price.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-slate-800">
                <div className="text-xs text-slate-400">Total projected:</div>
                <div className="font-mono font-bold text-emerald-300 text-lg" data-testid="vin-preview-total">
                  ${preview.report.total_projected_price.toFixed(2)}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
            {preview && existingReportId && (
              <Button
                onClick={() => commit("merge")}
                disabled={busy}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="vin-merge-btn"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Plus className="w-3 h-3 mr-1.5" />}
                Merge into this report
              </Button>
            )}
            {preview && (
              <Button
                onClick={() => commit("new")}
                disabled={busy}
                className="bg-cyan-600 hover:bg-cyan-500"
                data-testid="vin-create-btn"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FileCheck className="w-3 h-3 mr-1.5" />}
                Create new report
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────
// EditCustomerDialog — inline edit of customer/body_shop/insurance/RO on an existing report
// Also updates the linked Job (via backend cascade) so the Invoice recipient stays in sync.
// ─────────────────────────────────────────────────────────────
function EditCustomerDialog({ open, onClose, reportId, current, onSaved }) {
  const [customer, setCustomer] = useState("");
  const [bodyShop, setBodyShop] = useState("");
  const [insurance, setInsurance] = useState("");
  const [adjuster, setAdjuster] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [roNumber, setRoNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setCustomer(current?.customer || "");
      setBodyShop(current?.body_shop || "");
      setInsurance(current?.insurance || "");
      setAdjuster(current?.adjuster || "");
      setClaimNumber(current?.claim_number || "");
      setPolicyNumber(current?.policy_number || "");
      setRoNumber(current?.ro_number || "");
      (async () => {
        try {
          const [crm, mc, bs] = await Promise.all([
            api.get("/crm/contacts?type=customer"),
            api.get("/master/customers"),
            api.get("/master/body-shops"),
          ]);
          const items = [];
          const seen = new Set();
          const add = (v) => { if (v && v.id && !seen.has(v.id)) { seen.add(v.id); items.push(v); } };
          (bs.data || []).filter((s) => !s.archived).forEach((s) => add({ id: s.id, kind: "body_shop", name: s.company_name }));
          (crm.data || []).forEach((c) => add({ id: c.id, kind: "customer", name: c.name }));
          (mc.data || []).filter((c) => !c.archived).forEach((c) => add({
            id: c.id, kind: "customer",
            name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—",
          }));
          setContacts(items);
        } catch { /* noop */ }
      })();
    }
    if (!open) { setShowPicker(false); setSearch(""); }
  }, [open, current]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/calibration-reports/${reportId}/customer`, {
        customer: customer || null,
        body_shop: bodyShop || null,
        insurance: insurance || null,
        adjuster: adjuster || null,
        claim_number: claimNumber || null,
        policy_number: policyNumber || null,
        ro_number: roNumber || null,
      });
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar");
    } finally { setBusy(false); }
  };

  const pick = (c) => {
    if (c.kind === "body_shop") { setBodyShop(c.name); }
    else { setCustomer(c.name); }
    setShowPicker(false); setSearch("");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-[#0B1120] border-slate-800" data-testid="edit-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-300">
            <Pencil className="w-4 h-4" /> Editar Cliente / Destinatário
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Alterar aqui atualiza o Report + o Job vinculado + a Invoice futura. Todos ficam em sincronia.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="w-full text-left rounded-md bg-slate-900/50 border border-dashed border-slate-700 hover:border-blue-500 px-3 py-2 text-xs text-slate-400 hover:text-blue-300 transition-colors"
            data-testid="edit-pick-contact-btn"
          >
            <User className="w-3.5 h-3.5 inline mr-1.5" />
            {showPicker ? "Fechar seletor" : "Escolher da lista de contatos cadastrados"}
          </button>
          {showPicker && (
            <div className="rounded-md border border-slate-700 bg-slate-900/60 p-2 space-y-1.5">
              <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-slate-900 border-slate-700 text-xs h-8" autoFocus />
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {contacts
                  .filter((c) => !search || (c.name || "").toLowerCase().includes(search.toLowerCase()))
                  .slice(0, 20)
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => pick(c)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-800 flex items-center justify-between text-xs"
                    >
                      <div className="text-slate-200">{c.name}</div>
                      <Badge className={c.kind === "body_shop"
                        ? "bg-orange-500/15 border border-orange-500/40 text-orange-200 text-[9px]"
                        : "bg-blue-500/15 border border-blue-500/40 text-blue-200 text-[9px]"}
                      >
                        {c.kind === "body_shop" ? "SHOP" : "CLIENT"}
                      </Badge>
                    </button>
                  ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Cliente</Label>
              <Input value={customer} onChange={(e) => setCustomer(e.target.value)} className="mt-1 bg-slate-900 border-slate-700" data-testid="edit-customer-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Body Shop</Label>
              <Input value={bodyShop} onChange={(e) => setBodyShop(e.target.value)} className="mt-1 bg-slate-900 border-slate-700" data-testid="edit-bodyshop-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Insurance</Label>
              <Input value={insurance} onChange={(e) => setInsurance(e.target.value)} className="mt-1 bg-slate-900 border-slate-700" data-testid="edit-insurance-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Adjuster</Label>
              <Input value={adjuster} onChange={(e) => setAdjuster(e.target.value)} className="mt-1 bg-slate-900 border-slate-700" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Claim #</Label>
              <Input value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Policy #</Label>
              <Input value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 font-mono" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase font-bold text-slate-500">RO / PO #</Label>
              <Input value={roNumber} onChange={(e) => setRoNumber(e.target.value)} className="mt-1 bg-slate-900 border-slate-700 font-mono" data-testid="edit-ro-input" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-500" data-testid="edit-customer-save-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Check className="w-3 h-3 mr-1.5" />}
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// PreScanDialog — upload pre-scan PDF, AI extracts DTCs → suggests calibrations
// Policy iter 44: only inside existing report; non-ADAS = informative only;
// full pre+post PDFs → extract only pre-scan section.
// ─────────────────────────────────────────────────────────────
function PreScanDialog({ open, onClose, reportId, jobId, onCommitted }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | uploading | analyzing | done
  const [result, setResult] = useState(null);
  const [selectedOps, setSelectedOps] = useState(new Set());

  useEffect(() => {
    if (!open) {
      setFile(null); setBusy(false); setPhase("idle"); setResult(null); setSelectedOps(new Set());
    }
  }, [open]);

  const analyze = async () => {
    if (!file) return toast.error("Solte um PDF primeiro");
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Só PDF é aceito");
    setBusy(true); setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      setPhase("analyzing");
      const r = await api.post(`/prescan/analyze?report_id=${reportId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setResult(r.data); setPhase("done");
      // Auto-select all ops with confidence >= 80
      const auto = new Set(
        (r.data.required_calibrations || [])
          .filter((op) => (op.confidence || 0) >= 80)
          .map((op) => op.op_key)
      );
      setSelectedOps(auto);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao analisar");
      setPhase("idle");
    } finally { setBusy(false); }
  };

  const toggleOp = (key) => {
    const next = new Set(selectedOps);
    next.has(key) ? next.delete(key) : next.add(key);
    setSelectedOps(next);
  };

  const commit = async () => {
    if (!selectedOps.size) return toast.error("Selecione ao menos 1 operação");
    setBusy(true);
    try {
      await api.post(`/prescan/${result.id}/add-to-report`, { op_keys: [...selectedOps] });
      onCommitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setBusy(false); }
  };

  const commitDirectQuote = async () => {
    if (!selectedOps.size) return toast.error("Selecione ao menos 1 operação");
    if (!window.confirm(`Gerar Orçamento direto (pula o Report)?\n\nCria uma Quote em "pending_approval" com ${selectedOps.size} operação(ões).`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/prescan/${result.id}/generate-quote-direct`, { op_keys: [...selectedOps] });
      toast.success(`Quote ${r.data.quote_number} criada · $${r.data.total.toFixed(2)}`);
      onClose?.();
      window.location.href = `/quotes/${r.data.quote_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="prescan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-300">
            <Wrench className="w-4 h-4" /> Pre-Scan Analysis
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            Drop the scan PDF (Autel/Bosch/Snap-On/Launch). The system extracts DTCs, identifies which calibrations are required, and you approve before adding to the Report.
          </DialogDescription>
        </DialogHeader>

        {phase !== "done" && (
          <div className="space-y-3">
            <label className="block rounded-lg border-2 border-dashed border-slate-700 hover:border-amber-500 p-8 text-center cursor-pointer transition-colors" data-testid="prescan-drop">
              <input type="file" accept=".pdf" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0])} />
              <FileCheck className="w-8 h-8 mx-auto text-slate-500 mb-2" />
              <div className="text-sm text-slate-300">
                {file ? <span className="text-amber-300 font-semibold">{file.name}</span> : "Click or drag the PDF here"}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Max 15 MB · PDF only</div>
            </label>
            {phase !== "idle" && (
              <div className="rounded-md bg-slate-900 border border-slate-800 p-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-amber-300" />
                <span className="text-xs text-slate-300">
                  {phase === "uploading" ? "Uploading PDF..." : "Analyzing with AI (10-30s)..."}
                </span>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
              <Button onClick={analyze} disabled={busy || !file} className="bg-amber-600 hover:bg-amber-500" data-testid="prescan-analyze-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Sparkles className="w-3 h-3 mr-1.5" />}
                Analyze
              </Button>
            </div>
          </div>
        )}

        {phase === "done" && result && (
          <div className="space-y-4">
            {/* Vehicle + confidence */}
            <div className="rounded-md bg-cyan-500/5 border border-cyan-500/30 p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <div className="text-[10px] uppercase text-slate-500">Vehicle detected</div>
                  <div className="font-semibold text-cyan-200">
                    {result.vehicle?.year} {result.vehicle?.make} {result.vehicle?.model}
                    {result.vehicle?.vin && <span className="ml-2 font-mono text-xs text-slate-400">VIN: {result.vehicle.vin}</span>}
                  </div>
                </div>
                <Badge className={`text-[10px] ${result.confidence >= 90 ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-200" : result.confidence >= 70 ? "bg-amber-500/15 border border-amber-500/40 text-amber-200" : "bg-rose-500/15 border border-rose-500/40 text-rose-200"}`}>
                  {result.confidence}% confidence · {result.scan_type?.replace("_", " ").toUpperCase()}
                  {result.scan_brand && result.scan_brand !== "unknown" && <span className="ml-1.5">· {result.scan_brand.toUpperCase()}</span>}
                </Badge>
              </div>
              {result.fallback_used && (
                <div className="mt-2 text-[10px] text-rose-300 flex items-center gap-1.5" data-testid="prescan-fallback-badge">
                  ⚠ Fallback mode (AI offline) — regex extraction, please review manually
                </div>
              )}
            </div>

            {/* Recommendation (English) */}
            {(result.recommendation || result.recommendation_pt) && (
              <div className="rounded-md bg-slate-900/60 border border-slate-800 p-3 text-xs text-slate-300 italic" data-testid="prescan-recommendation">
                {result.recommendation || result.recommendation_pt}
              </div>
            )}

            {/* Required calibrations - selectable */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-300 mb-2">
                Suggested Calibrations ({result.required_calibrations?.length || 0})
              </div>
              {result.no_calibration_needed ? (
                <div className="rounded-md bg-emerald-500/10 border border-emerald-500/40 p-3 text-sm text-emerald-200" data-testid="prescan-no-cal">
                  ✓ No calibration required. {result.no_calibration_reason || result.no_calibration_reason_pt}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {(result.required_calibrations || []).map((op) => (
                    <label
                      key={op.op_key}
                      className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors ${selectedOps.has(op.op_key) ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-900/40 hover:border-slate-600"}`}
                      data-testid={`prescan-op-${op.op_key}`}
                    >
                      <input
                        type="checkbox" checked={selectedOps.has(op.op_key)}
                        onChange={() => toggleOp(op.op_key)}
                        className="mt-1 accent-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold text-slate-100">{op.op_name}</div>
                          <Badge className="bg-slate-800 border-slate-600 text-[9px] uppercase">{op.cal_type}</Badge>
                          <span className={`text-[10px] font-mono ${op.confidence >= 90 ? "text-emerald-400" : op.confidence >= 70 ? "text-amber-400" : "text-rose-400"}`}>
                            {op.confidence}%
                          </span>
                          {!op.priced_from_rate_table && (
                            <Badge className="bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[9px]">NOT PRICED IN RATE TABLE</Badge>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{op.reason || op.reason_pt}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono">DTCs: {(op.reason_dtcs || []).join(", ") || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-emerald-400 font-semibold">${(op.price || 0).toFixed(2)}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Non-ADAS findings — informative only */}
            {result.non_adas_findings?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                  Non-ADAS DTCs — Informational (send to mechanic)
                </div>
                <div className="space-y-1">
                  {result.non_adas_findings.map((f, i) => (
                    <div key={i} className="rounded bg-slate-900/40 border border-slate-800 p-2 text-[11px]" data-testid={`prescan-nonadas-${i}`}>
                      <div className="flex justify-between gap-2">
                        <div>
                          <span className="font-mono text-rose-300">{f.dtc}</span> <span className="text-slate-500">·</span> <span className="text-slate-400">{f.module}</span>
                        </div>
                      </div>
                      <div className="text-slate-300 mt-0.5">{f.description}</div>
                      <div className="text-amber-300 mt-0.5 italic">→ {f.action_needed || f.action_needed_pt}</div>
                      {reportId && f.dtc && (
                        <div className="mt-1">
                          <DtcHelpButton
                            jobId={jobId}
                            dtcCode={f.dtc}
                            module={f.module}
                            vehicleLabel={result?.vehicle ? `${result.vehicle.year || ""} ${result.vehicle.make || ""} ${result.vehicle.model || ""}`.trim() : null}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DTCs table */}
            {result.dtcs?.length > 0 && (
              <details className="text-xs">
                <summary className="text-slate-400 cursor-pointer hover:text-slate-200 text-[10px] uppercase tracking-wider font-bold">
                  View all DTCs ({result.dtcs.length})
                </summary>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                  {result.dtcs.map((d, i) => (
                    <div key={i} className="flex justify-between border-b border-slate-800/50 py-1 px-1 text-[11px]">
                      <div>
                        <span className="font-mono text-rose-300">{d.code}</span>
                        <span className="text-slate-500 mx-1.5">·</span>
                        <span className="text-slate-400">{d.module}</span>
                      </div>
                      <div className="text-slate-500">{d.description}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 flex-wrap">
              <Button variant="outline" className="border-slate-700" onClick={onClose}>Close</Button>
              <Button
                onClick={commitDirectQuote}
                disabled={busy || !selectedOps.size}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="prescan-direct-quote-btn"
                title="Creates a pending_approval Quote without going through the Report — use when you fully trust the analysis"
              >
                <FileCheck className="w-3 h-3 mr-1.5" />
                Generate Direct Quote
              </Button>
              <Button
                onClick={commit}
                disabled={busy || !selectedOps.size}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="prescan-commit-btn"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Plus className="w-3 h-3 mr-1.5" />}
                Add {selectedOps.size} to Report
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

